import React, { useState, useEffect, useCallback } from 'react';
import { api, useLanguage, useAuth } from '../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Shield, X, Swords } from 'lucide-react';

const TacticalShiftBanner = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [alert, setAlert] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const t = (fr, en) => (language === 'fr' ? fr : en);

  const fetchAlert = useCallback(async () => {
    try {
      const res = await api.get('/alerts/tactical-shift');
      if (res.data.alert) {
        const alertId = res.data.alert.created_at;
        const dismissedKey = `tactical_shift_dismissed_${alertId}`;
        if (!localStorage.getItem(dismissedKey)) {
          setAlert(res.data.alert);
        }
      }
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => { fetchAlert(); }, [fetchAlert]);

  const handleDismiss = async () => {
    if (alert?.created_at) {
      localStorage.setItem(`tactical_shift_dismissed_${alert.created_at}`, 'true');
    }
    setDismissed(true);
    try {
      await api.post('/alerts/tactical-shift/dismiss');
    } catch {
      // silent
    }
  };

  // Silence Radio: hide all gamification alerts
  if (user?.silence_radio) return null;
  if (!alert || dismissed) return null;

  const isOffensive = alert.direction === 'offensive' || alert.alert_type?.includes('ROUGE');
  const gradientFrom = isOffensive ? 'from-red-900/40' : 'from-blue-900/40';
  const gradientTo = isOffensive ? 'to-orange-900/20' : 'to-indigo-900/20';
  const borderColor = isOffensive ? 'border-red-500/50' : 'border-blue-500/50';
  const glowColor = isOffensive ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)';
  const IconComp = isOffensive ? AlertTriangle : Shield;
  const iconColor = isOffensive ? 'text-red-400' : 'text-blue-400';
  const titleColor = isOffensive ? 'text-red-300' : 'text-blue-300';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className={`mb-4 rounded-2xl border ${borderColor} bg-gradient-to-r ${gradientFrom} ${gradientTo} relative overflow-hidden`}
        style={{ boxShadow: `0 0 30px ${glowColor}` }}
        data-testid="tactical-shift-banner"
      >
        {/* Animated pulse background */}
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={{ opacity: [0.05, 0.15, 0.05] }}
          transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
          style={{ background: `radial-gradient(circle at 20% 50%, ${isOffensive ? 'rgba(239,68,68,0.3)' : 'rgba(59,130,246,0.3)'}, transparent 60%)` }}
        />

        <div className="relative p-4">
          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/10 transition-colors"
            data-testid="tactical-shift-dismiss"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <motion.div
              animate={{ rotate: [0, -5, 5, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            >
              <IconComp className={`w-6 h-6 ${iconColor}`} />
            </motion.div>
            <div>
              <p className={`text-[10px] uppercase tracking-[0.25em] ${iconColor}`}>
                {t('Alerte Tactique', 'Tactical Alert')}
              </p>
              <h3 className={`font-heading text-sm uppercase tracking-tight ${titleColor}`} data-testid="tactical-shift-title">
                {alert.alert_type || t('SHIFT TACTIQUE DETECTE', 'TACTICAL SHIFT DETECTED')}
              </h3>
            </div>
          </div>

          {/* Message */}
          <p className="text-sm text-foreground/90 leading-relaxed pr-6" data-testid="tactical-shift-message">
            {alert.message}
          </p>

          {/* Shift details */}
          {alert.shifts && alert.shifts.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {alert.shifts.slice(0, 3).map((shift, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-medium"
                  style={{
                    backgroundColor: shift.team_id === 'rouge' ? 'rgba(59,130,246,0.15)' : 'rgba(239,68,68,0.15)',
                    color: shift.team_id === 'rouge' ? '#93C5FD' : '#FCA5A5',
                  }}
                  data-testid={`shift-detail-${i}`}
                >
                  <Swords className="w-3 h-3" />
                  <span>{shift.team_id === 'rouge' ? '🔴' : '🔵'}</span>
                  <span>{shift.class_name}</span>
                  <span className="font-black">
                    {shift.delta > 0 ? '+' : ''}{shift.delta}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TacticalShiftBanner;
